import Debug from 'debug'

import { AbortError } from '@jotta/types/AbortError'
import { AppError } from '@jotta/types/AppError'
import type { ProgressHandler } from '@jotta/types/IO'
import { XidSchema } from '@jotta/types/schemas/ErrorSchema'

const debug = Debug('jotta:utils:fileUploadXhr')

export class XMLHttpError extends Error {
  status: number
  event: ProgressEvent<EventTarget>

  constructor({
    message,
    status,
    event,
  }: {
    message?: string
    status: number
    event: ProgressEvent<EventTarget>
  }) {
    super(message)
    this.status = status
    this.event = event
  }
}

export const fileUploadXhr = <T = unknown>(
  file: File,
  url: string,
  accessToken: string,
  xid: string,
  onProgress?: ProgressHandler,
  signal?: AbortSignal,
) =>
  new Promise<T>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new AbortError())
      return
    }

    const xhr = new XMLHttpRequest()
    let settled = false

    function cleanup() {
      if (signal) {
        signal.removeEventListener('abort', onAbort)
      }
    }

    function resolveOnce(value: T) {
      if (settled) {
        return
      }

      settled = true
      cleanup()
      resolve(value)
    }

    function rejectOnce(error: Error) {
      if (settled) {
        return
      }

      settled = true
      cleanup()
      reject(error)
    }

    function onAbort() {
      xhr.abort()
      rejectOnce(new AbortError())
    }

    function fileUploadXhrOnLoad() {
      const { statusText: message, status } = xhr
      if (status >= 300) {
        const parsed = XidSchema.safeParse(xhr.response)
        return rejectOnce(
          new AppError({
            message,
            HTTPStatus: status,
            xid: parsed.success ? parsed.data : xid,
          }),
        )
      }
      return resolveOnce(xhr.response)
    }
    function fileUploadXhrOnError(event: ProgressEvent<EventTarget>) {
      const { statusText: message, status } = xhr
      return rejectOnce(new XMLHttpError({ message, status, event }))
    }

    xhr.onload = fileUploadXhrOnLoad
    xhr.onerror = fileUploadXhrOnError
    if (signal) {
      signal.addEventListener('abort', onAbort)
    }
    xhr.overrideMimeType('application/octet-stream')
    xhr.open('POST', url)
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.setRequestHeader('x-id', xid)
    xhr.responseType = 'json'

    if (onProgress) {
      xhr.upload.addEventListener('progress', onProgress)
      // This is the last ProgressEvent of upload, but it happens before
      // the xhr.response is there, hence I'm removing it.
      // xhr.upload.onload = onload
      xhr.upload.onerror = fileUploadXhrOnError
      xhr.upload.ontimeout = fileUploadXhrOnError
    }

    xhr.send(file)
  })
